/**
 * A* on a rectangular grid: 8-connected, no corner cutting, octile heuristic, fully
 * deterministic (ties break on h, then on cell index).
 */

export interface NavGrid {
  width: number;
  height: number;
  /** length width*height, row-major (index = y * width + x); 1 = blocked, 0 = free. */
  blocked: Uint8Array;
  /** Optional per-cell cost multiplier (>= 1), same indexing. Missing = all 1. */
  cost?: Float32Array;
}

const NEIGHBOURS: ReadonlyArray<readonly [dx: number, dy: number]> = [
  [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
];

/**
 * Cells from start to goal inclusive, or null when there is no path, when either end is
 * out of bounds or blocked, or when more than `maxExpansions` nodes were expanded.
 */
export function findPath(
  grid: NavGrid,
  sx: number,
  sy: number,
  gx: number,
  gy: number,
  maxExpansions = 20000,
): Array<[number, number]> | null {
  const { width, height, blocked, cost } = grid;
  const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height;
  const free = (x: number, y: number): boolean => inBounds(x, y) && (blocked[y * width + x] ?? 1) === 0;
  if (!free(sx, sy) || !free(gx, gy)) return null;
  if (sx === gx && sy === gy) return [[sx, sy]];

  const size = width * height;
  const g = new Float64Array(size).fill(Infinity);
  const h = new Float64Array(size);
  const parent = new Int32Array(size).fill(-1);
  const closed = new Uint8Array(size);
  const heuristic = (x: number, y: number): number => {
    const dx = Math.abs(x - gx);
    const dy = Math.abs(y - gy);
    return dx + dy + (Math.SQRT2 - 2) * Math.min(dx, dy);
  };

  // Binary min-heap of cell indices with lazy deletion; key = (f, h, index).
  const heap: number[] = [];
  const heapF: number[] = [];
  const less = (i: number, j: number): boolean => {
    const fi = heapF[i] ?? 0;
    const fj = heapF[j] ?? 0;
    if (fi !== fj) return fi < fj;
    const ci = heap[i] ?? 0;
    const cj = heap[j] ?? 0;
    const hi = h[ci] ?? 0;
    const hj = h[cj] ?? 0;
    if (hi !== hj) return hi < hj;
    return ci < cj;
  };
  const swap = (i: number, j: number): void => {
    const c = heap[i] ?? 0;
    heap[i] = heap[j] ?? 0;
    heap[j] = c;
    const f = heapF[i] ?? 0;
    heapF[i] = heapF[j] ?? 0;
    heapF[j] = f;
  };
  const push = (cell: number, f: number): void => {
    heap.push(cell);
    heapF.push(f);
    let i = heap.length - 1;
    while (i > 0) {
      const up = (i - 1) >> 1;
      if (!less(i, up)) break;
      swap(i, up);
      i = up;
    }
  };
  const pop = (): number => {
    const top = heap[0] ?? -1;
    const lastCell = heap.pop();
    const lastF = heapF.pop();
    if (heap.length > 0 && lastCell !== undefined && lastF !== undefined) {
      heap[0] = lastCell;
      heapF[0] = lastF;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let best = i;
        if (l < heap.length && less(l, best)) best = l;
        if (r < heap.length && less(r, best)) best = r;
        if (best === i) break;
        swap(i, best);
        i = best;
      }
    }
    return top;
  };

  const start = sy * width + sx;
  const goal = gy * width + gx;
  g[start] = 0;
  h[start] = heuristic(sx, sy);
  push(start, h[start] ?? 0);

  let expansions = 0;
  while (heap.length > 0) {
    const current = pop();
    if (current < 0 || closed[current] === 1) continue;
    closed[current] = 1;
    if (current === goal) break;
    expansions++;
    if (expansions > maxExpansions) return null;

    const cx = current % width;
    const cy = (current - cx) / width;
    const gCurrent = g[current] ?? Infinity;
    for (const [dx, dy] of NEIGHBOURS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!free(nx, ny)) continue;
      if (dx !== 0 && dy !== 0 && (!free(nx, cy) || !free(cx, ny))) continue;
      const n = ny * width + nx;
      if (closed[n] === 1) continue;
      const stepCost = (dx !== 0 && dy !== 0 ? Math.SQRT2 : 1) * (cost ? (cost[n] ?? 1) : 1);
      const tentative = gCurrent + stepCost;
      if (tentative < (g[n] ?? Infinity)) {
        g[n] = tentative;
        parent[n] = current;
        h[n] = heuristic(nx, ny);
        push(n, tentative + (h[n] ?? 0));
      }
    }
  }

  if (closed[goal] !== 1) return null;
  const path: Array<[number, number]> = [];
  let cell = goal;
  while (cell >= 0) {
    const x = cell % width;
    path.push([x, (cell - x) / width]);
    cell = parent[cell] ?? -1;
  }
  return path.reverse();
}
